import React, { useEffect, useMemo } from 'react';
import { Card, Button, Tag } from 'antd';
import { FieldArray } from 'formik';
import { Input } from 'formik-antd';
import { useTranslation } from 'react-i18next';
import { FaPlusCircle, FaRegTimesCircle } from 'react-icons/fa';
import FormControl from '~/components/Form/FormControl';
import InputCurrency from '~/components/Form/InputCurrency';
import Row from '~/components/Row';
import { useCheckAuth } from '~/components/Can/checkAuth';
import { formatDecimal, formatPrice, parseCommaToDot, parsePrice } from '~/Utils/format';
import TaxesSection from './sections/taxesSection';
import RevenuesSection from './sections/RevenuesSection';

export default function FinancialClosingRevenues({ values, setFieldValue, cardsOperators, handleTitle }) {
  const { t } = useTranslation();
  const authCheck = useCheckAuth();

  const fluidPermissions = () => {
    return authCheck('@events/financialClosingEdit');
  };

  const calculatedTotals = useMemo(() => {
    const rawDebit = parsePrice(values.debit_raw_price || '0');
    const rawCredit = parsePrice(values.credit_raw_price || '0');
    const rawPix = parsePrice(values.pix_raw_price || '0');

    const feeDebit = (parseCommaToDot(values.debit_fee || '0') / 100) * rawDebit;
    const feeCredit = (parseCommaToDot(values.credit_fee || '0') / 100) * rawCredit;
    const feePix = (parseCommaToDot(values.pix_fee || '0') / 100) * rawPix;

    const liqDebit = rawDebit - feeDebit;
    const liqCredit = rawCredit - feeCredit;
    const liqPix = rawPix - feePix;

    const totalRaw = rawDebit + rawCredit + rawPix;
    const totalLiquid = liqDebit + liqCredit + liqPix;

    const totalPayments =
      values?.event_financial_payments?.reduce((acc, p) => acc + parsePrice(p.price || '0'), 0) || 0;
    const totalLosses = values?.event_financial_losses?.reduce((acc, l) => acc + parsePrice(l.price || '0'), 0) || 0;

    const customerFinal = totalLiquid - totalPayments;
    const totalAgio =
      values?.revenues?.reduce((acc, r) => {
        const varAcc = acc + parsePrice(r.agio || '0');
        return varAcc;
      }, 0) || 0;
    const revTotalFinal = values?.revenues?.reduce((acc, r) => acc + parsePrice(r.final_price || '0'), 0) || 0;
    const realFinal = revTotalFinal - totalPayments;

    return {
      feeDebit,
      feeCredit,
      feePix,
      liqDebit,
      liqCredit,
      liqPix,
      totalRaw,
      totalLiquid,
      totalPayments,
      totalLosses,
      customerFinal,
      totalAgio,
      revTotalFinal,
      realFinal,
    };
  }, [
    values.debit_raw_price,
    values.credit_raw_price,
    values.pix_raw_price,
    values.debit_fee,
    values.credit_fee,
    values.pix_fee,
    values.event_financial_payments,
    values.event_financial_losses,
    values.revenues,
  ]);

  useEffect(() => {
    setFieldValue('total_raw_price', formatDecimal(calculatedTotals.totalRaw));
    setFieldValue('debit_final_price', formatDecimal(calculatedTotals.liqDebit));
    setFieldValue('credit_final_price', formatDecimal(calculatedTotals.liqCredit));
    setFieldValue('pix_final_price', formatDecimal(calculatedTotals.liqPix));
    setFieldValue('total_liquid_client', formatDecimal(calculatedTotals.totalLiquid));
    setFieldValue('debit_fee_price', formatDecimal(calculatedTotals.feeDebit));
    setFieldValue('credit_fee_price', formatDecimal(calculatedTotals.feeCredit));
    setFieldValue('pix_fee_price', formatDecimal(calculatedTotals.feePix));
    setFieldValue('customer_final_price', formatDecimal(calculatedTotals.customerFinal));
    setFieldValue('payments_price', formatPrice(calculatedTotals.totalPayments));
    setFieldValue('losses_price', formatPrice(calculatedTotals.totalLosses));
    setFieldValue('agio_total', formatDecimal(calculatedTotals.totalAgio));
    setFieldValue('revenues_price', formatDecimal(calculatedTotals.revTotalFinal));
    setFieldValue('final_price', formatDecimal(calculatedTotals.realFinal));
  }, [calculatedTotals, setFieldValue]);

  return (
    <>
      <TaxesSection fluidPermissions={fluidPermissions} />
      <Row style={{ marginBottom: 20, marginTop: 20 }}>
        <h2>{t('Valores')}</h2>
      </Row>
      <Row>
        <FormControl cols={{ sm: 8, xs: 12 }} label={t('Débito bruto')} field="debit_raw_price">
          <InputCurrency name="debit_raw_price" disabled={!fluidPermissions()} />
        </FormControl>
        <FormControl
          cols={{ sm: 8, xs: 12 }}
          label={t('Taxa débito - ') + (values?.debit_fee || '0') + '%'}
          field="debit_fee_price"
        >
          <InputCurrency name="debit_fee_price" disabled />
        </FormControl>
        <FormControl cols={{ sm: 8, xs: 24 }} label={t('Débito líquido')} field="debit_final_price">
          <InputCurrency name="debit_final_price" disabled />
        </FormControl>
      </Row>
      <Row>
        <FormControl cols={{ sm: 8, xs: 12 }} label={t('Crédito bruto')} field="credit_raw_price">
          <InputCurrency name="credit_raw_price" disabled={!fluidPermissions()} />
        </FormControl>
        <FormControl
          cols={{ sm: 8, xs: 12 }}
          label={t('Taxa crédito - ') + (values?.credit_fee || '0') + '%'}
          field="credit_fee_price"
        >
          <InputCurrency name="credit_fee_price" disabled />
        </FormControl>
        <FormControl cols={{ sm: 8, xs: 24 }} label={t('Crédito líquido')} field="credit_final_price">
          <InputCurrency name="credit_final_price" disabled />
        </FormControl>
      </Row>
      <Row>
        <FormControl cols={{ sm: 8, xs: 12 }} label={t('Pix bruto')} field="pix_raw_price">
          <InputCurrency name="pix_raw_price" disabled={!fluidPermissions()} />
        </FormControl>
        <FormControl
          cols={{ sm: 8, xs: 12 }}
          label={t('Taxa pix - ') + (values?.pix_fee || '0') + '%'}
          field="pix_fee_price"
        >
          <InputCurrency name="pix_fee_price" disabled />
        </FormControl>
        <FormControl cols={{ sm: 8, xs: 24 }} label={t('Pix líquido')} field="pix_final_price">
          <InputCurrency name="pix_final_price" disabled />
        </FormControl>
      </Row>

      <RevenuesSection
        values={values}
        cardsOperators={cardsOperators}
        fluidPermissions={fluidPermissions}
        handleTitle={handleTitle}
      />

      <Row style={{ marginBottom: 20, marginTop: 20 }}>
        <h2>{t('Total')}</h2>
      </Row>
      <Row>
        <FormControl
          cols={{ sm: 12, xs: 12 }}
          label={t('Total bruto (débito + crédito + pix)')}
          field="total_raw_price"
        >
          <InputCurrency name="total_raw_price" disabled />
        </FormControl>
        <FormControl
          cols={{ sm: 12, xs: 12 }}
          label={t('Total líquido (débito + crédito + pix)')}
          field="total_liquid_client"
        >
          <InputCurrency name="total_liquid_client" disabled />
        </FormControl>
      </Row>
      <Row>
        <FormControl cols={{ sm: 12, xs: 12 }} label={t('Total líquido real')} field="revenues_price">
          <InputCurrency name="revenues_price" disabled />
        </FormControl>
        <FormControl cols={{ sm: 12, xs: 12 }} label={t('Ágio de taxa admninistrativa total')} field="agio_total">
          <InputCurrency name="agio_total" disabled />
        </FormControl>
      </Row>

      <Card
        title={t('Sobra/Falta de dinheiro')}
        extra={
          <Tag>
            {t('Total')}: {formatPrice(calculatedTotals.totalLosses)}
          </Tag>
        }
      >
        <FieldArray
          key={'event_financial_losses'}
          name="event_financial_losses"
          render={(arrayHelpers) => (
            <>
              {values?.event_financial_losses?.map((efl, index) => (
                <Card
                  key={index}
                  style={{ marginBottom: 16, marginTop: 30 }}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{efl?.name}</span>
                      <Button danger type="text" onClick={() => arrayHelpers.remove(index)}>
                        <FaRegTimesCircle size={18} disabled={!fluidPermissions()} />
                      </Button>
                    </div>
                  }
                >
                  <Row>
                    <FormControl
                      cols={{ sm: 12, xs: 24 }}
                      label={t('Descrição')}
                      field={`event_financial_losses.${index}.name`}
                    >
                      <Input name={`event_financial_losses.${index}.name`} disabled={!fluidPermissions()} />
                    </FormControl>
                    <FormControl
                      cols={{ sm: 12, xs: 24 }}
                      label={t('Valor (R$)')}
                      field={`event_financial_losses.${index}.price`}
                    >
                      <InputCurrency name={`event_financial_losses.${index}.price`} disabled={!fluidPermissions()} />
                    </FormControl>
                  </Row>
                </Card>
              ))}
              <Button
                color="primary"
                icon={<FaPlusCircle color="green" />}
                onClick={() => arrayHelpers.push({ event_financial_id: null, name: '', price: '0,00' })}
                style={{ marginTop: 30 }}
                disabled={!fluidPermissions()}
              />
            </>
          )}
        />
      </Card>

      <Card
        title={t('Pagamentos')}
        extra={
          <Tag>
            {t('Total')}: {formatPrice(calculatedTotals.totalPayments)}
          </Tag>
        }
      >
        <FieldArray
          key={'event_financial_payments'}
          name="event_financial_payments"
          render={(arrayHelpers) => (
            <>
              {values?.event_financial_payments?.map((efp, index) => (
                <Card
                  key={index}
                  style={{ marginBottom: 16, marginTop: 30 }}
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{efp?.name}</span>
                      <Button
                        danger
                        type="text"
                        onClick={() => arrayHelpers.remove(index)}
                        disabled={!fluidPermissions()}
                      >
                        <FaRegTimesCircle size={18} />
                      </Button>
                    </div>
                  }
                >
                  <Row>
                    <FormControl
                      cols={{ sm: 12, xs: 24 }}
                      label={t('Descrição')}
                      field={`event_financial_payments.${index}.name`}
                    >
                      <Input name={`event_financial_payments.${index}.name`} disabled={!fluidPermissions()} />
                    </FormControl>
                    <FormControl
                      cols={{ sm: 12, xs: 24 }}
                      label={t('Valor (R$)')}
                      field={`event_financial_payments.${index}.price`}
                    >
                      <InputCurrency name={`event_financial_payments.${index}.price`} disabled={!fluidPermissions()} />
                    </FormControl>
                  </Row>
                </Card>
              ))}
              <Button
                color="primary"
                icon={<FaPlusCircle color="green" />}
                onClick={() => arrayHelpers.push({ event_financial_id: null, name: '', price: '0,00' })}
                style={{ marginTop: 30 }}
                disabled={!fluidPermissions()}
              />
            </>
          )}
        />
      </Card>

      <Row style={{ marginBottom: 20, marginTop: 20 }}>
        <h2>{t('Total final')}</h2>
      </Row>
      <Row>
        {/* <FormControl cols={{ sm: 12, xs: 12 }} field="final_price" label={t('Real')}>
          <InputCurrency disabled={!fluidPermissions()} name={`final_price`} />
        </FormControl> */}
        <FormControl cols={{ sm: 24, xs: 24 }} field="customer_final_price">
          <InputCurrency disabled name={`customer_final_price`} />
        </FormControl>
      </Row>
    </>
  );
}
